import './table.scss';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ClbCheckbox } from '@celebration/design-system/dist/lib/checkbox';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { useTranslation } from 'react-i18next';
import { Icons } from '@celebration/design-system/dist/lib/icon/props.types';
import ITableProps from './interfaces/ITableProps';
import Text from '../text';
import ButtonIcon from '../button-icon';
import TableContainer, {
    TableContainerSlots,
} from './components/table.container';
import Table from './components/table';
import TableEmpty from './components/table.empty';
import TableFooter, { TableFooterSlots } from './components/table.footer';
import TableHead from './components/table.thead';
import TableHeadRow from './components/table.thead.row';
import TableHeadCell, {
    TableTHeadCellSlots,
} from './components/table.thead.cell';
import TableBody from './components/table.tbody';
import TableBodyRow from './components/table.tbody.row';
import TableBodyCell from './components/table.tbody.cell';
import ITableHead from './interfaces/ITableHead';
import ToolbarTable from '../toolbar-table';
import i18n from '../../../lib/i18n';

function TableComponent({
    content: { data, totalItems },
    headers,
    fetchingData = false,
    toolbarIconsActions,
    toolbarButtonList,
    toolbarSearchCallback,
    emptyTableDescription = i18n.t('NenhumaInformacaoExibir'),
    loadMoreButtonCallback,
    itemButtonActions,
    selectRowCallback,
    selectAllCallback,
    hideToolbarTable,
    dataTestid,
    disablePagination = false,
}: Readonly<ITableProps>) {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState<string>();

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm !== undefined) {
                toolbarSearchCallback?.(searchTerm);
            }
        }, 1000);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, toolbarSearchCallback]);

    const renderEmptyTable = useMemo(() => {
        if (!data.length && !fetchingData) {
            return (
                <TableEmpty
                    slot={TableContainerSlots.table}
                    descriptionEmpty={emptyTableDescription}
                    className="table-empty"
                    data-testid="table-empty"
                />
            );
        }
    }, [data, emptyTableDescription, fetchingData]);

    const paginationText = useMemo(() => {
        return `${t('Mostrando')} 1-${data.length} ${t('de')} ${totalItems} ${t('ResultadoMinusculo')}`;
    }, [data, totalItems, t]);

    const renderTableFooter = useMemo(() => {
        return (
            <TableFooter
                slot={TableContainerSlots.footer}
                loadMoreVisible={
                    !disablePagination &&
                    data.length < totalItems &&
                    !fetchingData
                }
                loadMoreLabel={t('ExibirMais')}
                onClbLoadMore={() => loadMoreButtonCallback?.()}
            >
                <div
                    className="pagination-wrapper"
                    slot={TableFooterSlots.pagination}
                >
                    {!disablePagination && !!data.length && (
                        <Text value={paginationText} />
                    )}

                    {fetchingData && <ClbLoading size="sm" />}
                </div>
            </TableFooter>
        );
    }, [
        data,
        totalItems,
        loadMoreButtonCallback,
        paginationText,
        fetchingData,
        t,
        disablePagination,
    ]);

    const isAllChecked = useCallback(() => {
        if (data.length === 0) {
            return false;
        }

        return data.every(item => item.checked);
    }, [data]);

    const sortItem = (e: Event, item: ITableHead) => {
        if (!item.sortChange) {
            return;
        }

        item.sortChange(e);
    };

    const renderTableHead = useMemo(() => {
        return (
            <TableHead className="table-head" data-testid="table-head">
                <TableHeadRow
                    checked={isAllChecked()}
                    selectable={!!selectAllCallback}
                    onClbSelectTheadRow={e =>
                        selectAllCallback?.(!(e.target as ClbCheckbox).checked)
                    }
                >
                    {headers.map(item => (
                        <TableHeadCell
                            sortable={item.sortable}
                            onClbSortChange={e => sortItem(e, item)}
                            key={`tableHeadCell@${item.id}`}
                            id={item.id}
                            style={{ textAlign: item.alignContent }}
                        >
                            <Text
                                slot={TableTHeadCellSlots.content}
                                value={item.label}
                            />
                        </TableHeadCell>
                    ))}

                    {!!itemButtonActions?.length && <TableHeadCell />}
                </TableHeadRow>
            </TableHead>
        );
    }, [isAllChecked, selectAllCallback, headers, itemButtonActions?.length]);

    const renderCell = useCallback(
        (elementData: any) => {
            return headers?.map((headerCell: ITableHead, index: number) => {
                let cellKey;
                let cellContent;
                let className = 'table-cell';

                Object.keys(elementData).forEach((elementAttribute: string) => {
                    if (elementAttribute === headerCell.field) {
                        cellKey = String(index) + elementData.id;

                        if (
                            typeof elementData[elementAttribute] === 'string' ||
                            typeof elementData[elementAttribute] === 'number'
                        ) {
                            cellContent = (
                                <Text
                                    value={elementData[
                                        elementAttribute
                                    ].toString()}
                                />
                            );
                        } else {
                            cellContent = elementData[elementAttribute];
                        }
                    }
                });

                if (
                    headerCell.field === undefined &&
                    headerCell.contentIsButton
                ) {
                    cellKey = `custonButtons@${elementData.id}`;
                    className = 'table-cell-buttons';
                    cellContent = elementData.rowButtons;
                }

                return (
                    <TableBodyCell
                        key={`cell@${cellKey}`}
                        className={className}
                        style={{ textAlign: headerCell.alignContent }}
                    >
                        {cellContent}
                    </TableBodyCell>
                );
            });
        },
        [headers],
    );

    const renderItemActionsButtons = useCallback(
        (item: any) => {
            if (itemButtonActions?.length) {
                return itemButtonActions.map(buttonIcon => {
                    return (
                        <ButtonIcon
                            key={`item${item.id}@${buttonIcon.icon}`}
                            icon={buttonIcon.icon as Icons}
                            disabled={
                                buttonIcon.disabled ||
                                item.id === null ||
                                item.id.length === 0 ||
                                (item.active !== undefined && !item.active) ||
                                (item.isAmbevUser !== undefined &&
                                    !item.isAmbevUser &&
                                    buttonIcon.icon !== 'Edit')
                            }
                            size="sm"
                            tooltipLabel={buttonIcon.tooltipLabel}
                            tooltipPosition="left"
                            onClbClick={() => buttonIcon.callback(item)}
                            data-testid="table-row-actions-button"
                        />
                    );
                });
            }
        },
        [itemButtonActions],
    );

    const renderActionsCell = useCallback(
        (item: any) => {
            if (itemButtonActions?.length) {
                return (
                    <TableBodyCell>
                        <div
                            className="row-actions"
                            data-testid="table-row-actions"
                        >
                            {!!itemButtonActions?.length &&
                                renderItemActionsButtons(item)}
                        </div>
                    </TableBodyCell>
                );
            }
        },
        [itemButtonActions, renderItemActionsButtons],
    );

    const renderTableBody = useMemo(() => {
        if (data.length) {
            return (
                <TableBody data-testid="table-body">
                    {data.map(item => (
                        <TableBodyRow
                            checked={item.checked}
                            key={`row@${item.id}`}
                            id={item.id}
                            selectable={!!selectRowCallback}
                            onClbSelectRow={() => selectRowCallback?.(item.id)}
                            data-testid="table-body-row"
                        >
                            {renderCell(item)}
                            {renderActionsCell(item)}
                        </TableBodyRow>
                    ))}
                </TableBody>
            );
        }
    }, [data, selectRowCallback, renderCell, renderActionsCell]);

    const renderTable = useMemo(() => {
        return (
            <Table
                className="table"
                data-testid={dataTestid ?? 'table-component'}
                slot={TableContainerSlots.table}
            >
                {renderTableHead}
                {renderTableBody}
            </Table>
        );
    }, [dataTestid, renderTableHead, renderTableBody]);

    return (
        <TableContainer className="table-container" maxHeight={0}>
            {!hideToolbarTable && (
                <ToolbarTable
                    toolbarButtonList={toolbarButtonList}
                    toolbarIconsActions={toolbarIconsActions}
                    toolbarSearchCallback={toolbarSearchCallback}
                    setSearchTerm={setSearchTerm}
                />
            )}
            {renderTable}
            {renderEmptyTable}
            {renderTableFooter}
        </TableContainer>
    );
}

export default TableComponent;
